import type { App } from '@hangar/shared';
import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { StatusPill, Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { BitriseDone } from './BitriseDone';
import { BitriseExisting } from './BitriseExisting';
import { BitriseProvisionForm } from './BitriseProvisionForm';
import { BitriseSteps } from './BitriseSteps';
import { hasRevoked, missingPrerequisites, offersRetry, panelBadge, showsIntro, stepsToShow, type PanelBadge } from './bitriseModel';
import { useBitrisePanel, type BitrisePanelState } from './useBitrisePanel';

export { BitriseSteps };

interface Props {
  app: App;
  /** The "Done" button of the finished state (Connect app closes its sheet). */
  onDone?: () => void;
  /** Smaller headings: the panel sits inside a sheet. */
  compact?: boolean;
}

function BadgePill({ badge }: { badge: PanelBadge }) {
  if (badge === 'done') return <StatusPill status="succeeded" label="On Bitrise" />;
  if (badge === 'failed') return <StatusPill status="failed" label="Needs attention" />;
  return badge === 'running' ? <StatusPill status="running" label="Creating" /> : <Tag>not created</Tag>;
}

function PanelHeading({ p, compact }: { p: BitrisePanelState; compact?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <h3 className={cn('font-semibold text-foreground', compact ? 'text-[13px]' : 'text-sm')}>Bitrise</h3>
      <BadgePill badge={panelBadge(p.done, p.data, p.prov.running)} />
    </div>
  );
}

function Prerequisites({ p }: { p: BitrisePanelState }) {
  const { src } = p;
  return (
    <div data-testid="bitrise-prerequisites" className="flex flex-col gap-1.5 text-[13px]">
      {missingPrerequisites(src.usable, src.bitriseAccounts, hasRevoked(src.connections.data)).map((m) => (
        <span key={m}>{m}</span>
      ))}
      <Button asChild size="sm" variant="outline" className="w-fit">
        <Link to="/integrations">
          Open Integrations
          <Icon name="arrow_forward" size={16} />
        </Link>
      </Button>
    </div>
  );
}

function RetryRow({ p }: { p: BitrisePanelState }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <GatedButton size="sm" data-testid="bitrise-retry" can={p.admin} loading={p.prov.provisioning} onClick={p.retry}>
        <Icon name="refresh" size={16} />
        Try again
      </GatedButton>
      <span className="text-xs text-subtle">It resumes at the failed step.</span>
    </div>
  );
}

function ProvisionAction({ app, p }: { app: App; p: BitrisePanelState }) {
  const { prov } = p;
  if (prov.existing) return <BitriseExisting apps={prov.existing} choice={prov.choice} onChoose={prov.setChoice} admin={p.admin} loading={prov.provisioning} onConfirm={p.confirmExisting} onCancel={prov.cancelExisting} />;
  if (offersRetry(p.data, prov.running)) return <RetryRow p={p} />;
  return <BitriseProvisionForm appId={app.id} p={p} />;
}

function ProvisionStep({ app, p }: { app: App; p: BitrisePanelState }) {
  const shown = stepsToShow(p.data, p.prov.running);
  return (
    <>
      {shown ? <BitriseSteps steps={shown.steps} running={shown.running} /> : null}
      <ProvisionAction app={app} p={p} />
    </>
  );
}

function SetupBody({ app, p }: { app: App; p: BitrisePanelState }) {
  const { src } = p;
  if (src.connections.isLoading || src.accounts.isLoading) return <p className="text-xs text-subtle">Loading connections…</p>;
  if (src.usable.length === 0 || src.bitriseAccounts.length === 0) return <Prerequisites p={p} />;
  return <ProvisionStep app={app} p={p} />;
}

function SetupView({ app, p, compact }: { app: App; p: BitrisePanelState; compact?: boolean }) {
  return (
    <div className="flex flex-col gap-3">
      {showsIntro(compact, p.data) ? (
        <p className="text-[13px] text-muted-foreground">
          Hangar creates the Bitrise app and its workflows from your Bitbucket repository, and delivers the source to every build. You never open bitrise.io.
        </p>
      ) : null}
      <SetupBody app={app} p={p} />
    </div>
  );
}

/**
 * "Create on Bitrise" for a native app (SPEC §9.3): pick the repository of the Bitbucket connection, and Hangar
 * creates the Bitrise app and its workflows (`POST /apps/{id}/bitrise/provision`), step by step. A step
 * that fails says what to do and "Try again" resumes there. When Bitrise already has an app for the repository the
 * person chooses to use it or create another. Once it is done, "Sync Bitrise workflows" re-uploads the generated
 * workflows after profiles change. Tokens are the only thing anyone pastes; bitrise.io is never opened.
 */
export function BitrisePanel({ app, onDone, compact }: Props) {
  const p = useBitrisePanel(app);
  if (p.hidden) return null;
  return (
    <section data-testid="bitrise-panel" data-status={p.data?.status ?? 'loading'} className="flex flex-col gap-3 rounded-lg border bg-secondary/40 p-3 sm:p-4" aria-label="Bitrise setup">
      <PanelHeading p={p} compact={compact} />
      {p.done && p.data ? <BitriseDone data={p.data} admin={p.admin} syncing={p.prov.syncing} onSync={() => void p.prov.doSync()} onDone={onDone} /> : <SetupView app={app} p={p} compact={compact} />}
      <ProblemAlert error={p.prov.problem} testId="bitrise-problem" />
    </section>
  );
}
